import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LanguageService } from '@app-core/services/language.service';
import { SessionService } from '@app-core/services/session.service';
import { SessionRepository } from '@app-core/state/session.repository';
import { lastValueFrom, retry, take } from 'rxjs';
import { CountryService } from 'src/app/entities/countries/state/country.service';
import { RoutePaths } from 'src/app/routes/route-paths.enum';

@Injectable({
  providedIn: 'root',
})
export class InitializationService {
  private readonly router = inject(Router);
  private readonly countryService = inject(CountryService);
  private readonly languageService = inject(LanguageService);
  private readonly sessionRepo = inject(SessionRepository);
  private readonly sessionService = inject(SessionService);

  /**
   * Runs all the necessary initializations for the app that need to happen before the app loads
   *
   * Note: the reason we use Promises here instead of observables is because angular hasn't yet added
   * support for using observables in app initialization in version 11
   */
  async init(): Promise<void> {
    try {
      /*
        We call this to save us a few unnecessary api calls when a user with an expired token
        arrives to the app and the only way to check if his token has indeed expired was to wait for any
        api call to return an error and only then we would logout the user.

        Here we just make one call and if this throws any error we just "logout" the user to avoid multiple 401 errors.

        If the user is not logged in, just skip calling the api.
      */
      if (await lastValueFrom(this.sessionRepo.isLoggedIn$.pipe(take(1)))) {
        await lastValueFrom(
          this.sessionService.getLoggedInUser().pipe(take(1)),
        );
      }
    } catch {
      // if this api throws an error it most likely means that the user's token expired so just clean up the store
      this.sessionRepo.resetStore();
    }

    try {
      await lastValueFrom(
        this.countryService.getCountries().pipe(take(1), retry(3)),
      );
      await lastValueFrom(
        this.languageService.getAllLanguages().pipe(take(1), retry(3)),
      );
    } catch {
      this.router.navigateByUrl(`/${RoutePaths.errorServer}`);
    }
  }
}
